"use client";

import React, { useState, useEffect } from "react";
import { useRouter } from 'next/navigation';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OTPInput } from "@/components/ui/otp-input";
import { sendPhoneOTP, verifyPhoneOTP } from "@/actions/auth";
import { toast } from "sonner";
import { Smartphone, ArrowLeft, RefreshCw, AlertCircle } from "lucide-react";
// Removed unused imports
import { IMaskInput } from "react-imask";

export function OTPLoginForm() {
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [errors, setErrors] = useState<{ phone?: string; otp?: string }>({});
    const [touched, setTouched] = useState<{ phone?: boolean; otp?: boolean }>({});
    const router = useRouter();

        // Validation functions
        const validatePhone = (value: string): string | undefined => {
            if (!value.trim()) {
                return 'Phone number is required';
            }

            // Check if the formatted phone number has the correct pattern
            const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;
            if (!phonePattern.test(value)) {
                return 'Please enter a valid 10-digit US phone number';
            }
            return undefined;
        };

    const validateOTP = (value: string): string | undefined => {
        if (!value.trim()) {
            return 'Verification code is required';
        }
        if (value.length !== 6) {
            return 'Please enter the complete 6-digit code';
        }
        return undefined;
    };

    const handlePhoneChange = (value: string) => {
        setPhone(value);
        setTouched(prev => ({ ...prev, phone: true }));
        
        // Clear error when user starts typing
        if (errors.phone) {
            setErrors(prev => ({ ...prev, phone: undefined }));
        }
    };

    const handleOTPChange = (value: string) => {
        setOtp(value);
        setTouched(prev => ({ ...prev, otp: true }));
        
        // Clear error when user starts typing
        if (errors.otp) {
            setErrors(prev => ({ ...prev, otp: undefined }));
        }
    };

    const handleSendCode = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate phone field
        const phoneError = validatePhone(phone);
        if (phoneError) {
            setErrors(prev => ({ ...prev, phone: phoneError }));
            setTouched(prev => ({ ...prev, phone: true }));
            return;
        }
        
        setIsLoading(true);

        try {
            // Convert formatted phone to E.164 format
            const cleanPhone = phone.replace(/\D/g, ''); // Remove all non-digits
            const formattedPhone = `+1${cleanPhone}`;
            
            if (cleanPhone.length !== 10) {
                toast.error("Please enter a valid 10-digit phone number");
                return;
            }
            await sendPhoneOTP({ phone: formattedPhone });
            toast.success("A 6-digit code has been sent to your phone!");
            setIsCodeSent(true);
            setCountdown(30);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : `Failed to send code to ${phone}`;
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        // Validate OTP
        const otpError = validateOTP(otp);
        if (otpError) {
            setErrors(prev => ({ ...prev, otp: otpError }));
            setTouched(prev => ({ ...prev, otp: true }));
            return;
        }
        
        setIsVerifying(true);

        try {
            // Convert formatted phone to E.164 format
            const cleanPhone = phone.replace(/\D/g, ''); // Remove all non-digits
            const formattedPhone = `+1${cleanPhone}`;
            
            if (cleanPhone.length !== 10) {
                toast.error("Please enter a valid 10-digit phone number");
                return;
            }
            
            await verifyPhoneOTP({ phone: formattedPhone, token: otp });
            toast.success("Successfully signed in! Redirecting...");
            router.push('/home');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Invalid code. Please try again.";
            toast.error(errorMessage);
            setOtp("");
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendCode = async () => {
        if (countdown > 0) return;
        setIsLoading(true);

        try {
            // Convert formatted phone to E.164 format
            const cleanPhone = phone.replace(/\D/g, ''); // Remove all non-digits
            const formattedPhone = `+1${cleanPhone}`;
            
            if (cleanPhone.length !== 10) {
                toast.error("Please enter a valid 10-digit phone number");
                return;
            }
            
            await sendPhoneOTP({ phone: formattedPhone });
            setCountdown(30);
            toast.success("New code sent!");
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to send new code";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    if (!isCodeSent) {
        return (
            <div className="w-full max-w-md mx-auto space-y-6">
                {/* Header */}
                <div className="text-center">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-2">Welcome to Mira Mace</h2>
                    <p className="text-gray-600">Sign in or create your account</p>
                </div>

                {/* Phone Input Form */}
                <form onSubmit={handleSendCode} className="space-y-4">
                        <div className="space-y-2">
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                US Mobile number
                            </label>

                            <div className="flex items-center gap-2">
                                <div className="flex items-center px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 text-sm font-medium h-12">
                                    +1
                                </div>
                                <IMaskInput
                                    id="phone"
                                    mask="(000) 000-0000"
                                    unmask={false}
                                    inputMode="tel"
                                    className={`flex-1 h-12 border rounded-md px-3 py-2 text-base bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                        touched.phone && errors.phone
                                            ? 'border-red-300 focus:ring-red-500'
                                            : 'border-gray-300'
                                    }`}
                                    placeholder="(555) 123-4567"
                                    value={phone}
                                    onAccept={(value) => handlePhoneChange(value)}
                                    disabled={isLoading}
                                    autoComplete="tel"
                                    aria-invalid={touched.phone && !!errors.phone}
                                />
                            </div>

                            {touched.phone && errors.phone && (
                                <p className="text-sm text-red-600 flex items-center gap-1">
                                    <AlertCircle className="h-4 w-4" />
                                    {errors.phone}
                                </p>
                            )}
                        </div>

                    <Button
                        type="submit"
                        disabled={isLoading || !phone.trim()}
                        className="w-full h-12 text-base font-medium"
                    >
                        {isLoading ? (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <Smartphone className="w-4 h-4 mr-2" />
                                Send Code
                            </>
                        )}
                    </Button>
                </form>
            </div>
        );
    }

    return (
        <div className="w-full max-w-md mx-auto space-y-6">
                {/* Header */}
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">Enter verification code</h2>
                    <p className="text-sm text-gray-600">Sent to +1 {phone}</p>
                </div>

            {/* OTP Form */}
            <div className="space-y-4">
                <OTPInput
                    value={otp}
                    onChange={handleOTPChange}
                    disabled={isVerifying}
                    length={6}
                    className={touched.otp && errors.otp ? 'border-red-300' : ''}
                />
                
                {touched.otp && errors.otp && (
                    <p className="text-sm text-red-600 flex items-center justify-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {errors.otp}
                    </p>
                )}

                <Button
                    className="w-full h-12 text-base font-medium"
                    onClick={handleVerifyCode}
                    disabled={isVerifying || otp.length !== 6}
                >
                    {isVerifying ? (
                        <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Verifying...
                        </>
                    ) : (
                        "Sign In"
                    )}
                </Button>
            </div>

            {/* Resend */}
            <div className="text-center">
                <button
                    onClick={handleResendCode}
                    disabled={countdown > 0 || isLoading}
                    className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                    {isLoading ? (
                        "Sending..."
                    ) : countdown > 0 ? (
                        `Resend in ${countdown}s`
                    ) : (
                        "Resend code"
                    )}
                </button>
            </div>

            {/* Back to phone input */}
            <div className="text-center">
                <button
                    onClick={() => {
                        setIsCodeSent(false);
                        setOtp("");
                        setCountdown(0);
                        setErrors({});
                        setTouched({});
                    }}
                    className="text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center mx-auto"
                >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Use different phone number
                </button>
            </div>
        </div>
    );
} 